import * as yup from 'yup';
import FormFieldType from 'src/shared/ui/components/Form/FormFieldFactory/FormFieldType';

const getSelectedCustomerTypeCode = ({ fields, customerTypeList }) => {
  if (fields?.customerTypeId) {
    return customerTypeList.filter((x) => x.id === fields.customerTypeId)[0]
      .code;
  }
};

const Status = [
  '_0_PENDING_CONTACT',
  '_1_CONTACT_PLANNED',
  'CONTACTED',
  'INTERESED',
  'NOT_INTERESED',
  'FINALIZED',
  'IN_PROCESS',
  'ACTIVATE'
];

const getStatusOptions = (fields) => {
  let status;

  if (isContractActive(fields)) {
    status = [
      'ACTIVE',
      '_0_ACTIVE_PENDING_CONTACT',
      '_1_ACTIVE_CONTACT_PLANNED'
    ];
  } else {
    status = Status;
  }
  const options = status.map((x) => {
    return {
      id: x,
      name: x
    };
  });

  return options;
};

const isCustomerTypeCompany = (fields, customerTypeList) =>
  getSelectedCustomerTypeCode({ fields, customerTypeList }) === 'COMPANY';

const isContractActive = (fields) => {
  return [
    'ACTIVE',
    '_0_ACTIVE_PENDING_CONTACT',
    '_1_ACTIVE_CONTACT_PLANNED'
  ].includes(fields?.status?.toUpperCase());
};

const Customer = ({ fields, customerTypeList, user }) => [
  {
    name: 'status',
    validation: yup.string().required('fieldRequired'),
    type: FormFieldType.SELECT,
    value: fields && fields.status ? fields.status : 'INTERESED',
    options: getStatusOptions(fields)
  },
  {
    name: 'contactPlannedAt',
    validation: yup.date().nullable(true),
    type: FormFieldType.DATE,
    value:
      fields && fields.contactPlannedAt
        ? fields.contactPlannedAt
        : new Date(new Date().setDate(new Date().getDate() + 1)),
    enabled: ['_1_CONTACT_PLANNED', '_1_ACTIVE_CONTACT_PLANNED'].includes(
      fields?.status?.toUpperCase()
    )
  },
  {
    name: 'customerTypeId',
    validation: yup.string(),
    type: FormFieldType.SELECT,
    value: fields && fields.customerTypeId ? fields.customerTypeId : '',
    options: customerTypeList.map((x) => {
      return { id: x.id, name: x.code };
    }),
    readonly: isContractActive(fields)
  },
  {
    name: 'isFleet',
    validation: yup.boolean(),
    type: FormFieldType.CHECKBOX,
    value:
      fields && fields.isFleet !== undefined && fields.isFleet !== null
        ? !!fields.isFleet
        : isCustomerTypeCompany(fields, customerTypeList)
  },
  {
    name: 'document',
    validation: isContractActive(fields)
      ? yup
          .string()
          .trim()
          .required('fieldRequired')
      : yup
          .string()
          .trim()
          .nullable(true),
    type: FormFieldType.TEXT_FIELD,
    value: fields && fields.document ? fields.document : '',
    readonly:
      isContractActive(fields) && !user?.scopes.includes('superadmin:customers')
  },
  {
    name: 'businessName',
    validation: isCustomerTypeCompany(fields, customerTypeList)
      ? yup.string().required('fieldRequired')
      : yup.string().nullable(true),
    type: FormFieldType.TEXT_FIELD,
    value: fields && fields.businessName ? fields.businessName : '',
    readonly:
      isContractActive(fields) &&
      !user?.scopes.includes('superadmin:customers'),
    enabled: isCustomerTypeCompany(fields, customerTypeList),
    fullWidth: true
  },
  {
    name: 'firstname',
    validation: yup.string().required('fieldRequired'),
    type: FormFieldType.TEXT_FIELD,
    value: fields && fields.firstname ? fields.firstname : '',
    readonly:
      isContractActive(fields) &&
      !user?.scopes.includes('superadmin:customers'),
    enabled: !isCustomerTypeCompany(fields, customerTypeList),
    fullWidth: true
  },
  {
    name: 'surnames',
    validation: yup.string().required('fieldRequired'),
    type: FormFieldType.TEXT_FIELD,
    value: fields && fields.surnames ? fields.surnames : '',
    readonly:
      isContractActive(fields) &&
      !user?.scopes.includes('superadmin:customers'),
    enabled: !isCustomerTypeCompany(fields, customerTypeList),
    fullWidth: true
  },
  {
    name: 'address',
    validation: isContractActive(fields)
      ? yup
          .string()
          .trim()
          .required('fieldRequired')
      : yup
          .string()
          .trim()
          .nullable(true),
    type: FormFieldType.TEXT_FIELD,
    value: fields && fields.address ? fields.address : '',
    fullWidth: true
  },
  {
    name: 'department',
    validation: isContractActive(fields)
      ? yup
          .string()
          .trim()
          .required('fieldRequired')
      : yup
          .string()
          .trim()
          .nullable(true),
    type: FormFieldType.TEXT_FIELD,
    value: fields && fields.department ? fields.department : ''
  },
  {
    name: 'city',
    validation: isContractActive(fields)
      ? yup
          .string()
          .trim()
          .required('fieldRequired')
      : yup
          .string()
          .trim()
          .nullable(true),
    type: FormFieldType.TEXT_FIELD,
    value: fields && fields.city ? fields.city : ''
  },
  {
    name: 'neighborhood',
    validation: isContractActive(fields)
      ? yup
          .string()
          .trim()
          .required('fieldRequired')
      : yup
          .string()
          .trim()
          .nullable(true),
    type: FormFieldType.TEXT_FIELD,
    value: fields && fields.neighborhood ? fields.neighborhood : ''
  },
  {
    name: 'phone1',
    validation:
      !isContractActive(fields) &&
      !isCustomerTypeCompany(fields, customerTypeList)
        ? yup
            .string()
            .trim()
            .required('fieldRequired')
        : yup.string().nullable(true),
    type: FormFieldType.TEXT_FIELD,
    value: fields && fields.phone1 ? fields.phone1 : '',
    enabled:
      !isContractActive(fields) &&
      !isCustomerTypeCompany(fields, customerTypeList)
  },
  {
    name: 'phone2',
    validation: yup
      .string()
      .trim()
      .nullable(true),
    type: FormFieldType.TEXT_FIELD,
    value: fields && fields.phone2 ? fields.phone2 : '',
    enabled:
      !isContractActive(fields) &&
      !isCustomerTypeCompany(fields, customerTypeList)
  },
  {
    name: 'email',
    validation: yup
      .string()
      .trim()
      .nullable(true),
    type: FormFieldType.TEXT_FIELD,
    value: fields && fields.email ? fields.email : '',
    enabled:
      !isContractActive(fields) &&
      !isCustomerTypeCompany(fields, customerTypeList),
    fullWidth: true
  },
  {
    name: 'zoneCode',
    validation: isContractActive(fields)
      ? yup
          .string()
          .trim()
          .required('fieldRequired')
      : yup
          .string()
          .trim()
          .nullable(true),
    type: FormFieldType.TEXT_FIELD,
    value: fields && fields.zoneCode ? fields.zoneCode : ''
  },
  {
    name: 'latitude',
    validation: yup
      .string()
      .trim()
      .nullable(true),
    type: FormFieldType.TEXT_FIELD,
    value: fields && fields.latitude ? fields.latitude : ''
  },
  {
    name: 'longitude',
    validation: yup
      .string()
      .trim()
      .nullable(true),
    type: FormFieldType.TEXT_FIELD,
    value: fields && fields.longitude ? fields.longitude : ''
  },
  {
    name: 'comments',
    validation: yup.string().nullable(true),
    type: FormFieldType.TEXT_AREA,
    value: fields && fields.comments ? fields.comments : '',
    fullWidth: true,
    readonly: true
  },
  {
    name: 'newComment',
    validation: yup.string().nullable(true),
    type: FormFieldType.TEXT_AREA,
    value: fields && fields.newComment ? fields.newComment : '',
    fullWidth: true
  }
];

export default Customer;
